"use client";

import React from "react";
import { useAtom } from "jotai";
import { X } from "lucide-react";
import { useOnborda, type CardComponentProps } from "onborda";
import { useParams, useRouter } from "next/navigation";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { onbordaAtom } from "@/data/onborda";
import { hrefPreservingPinnedChatLayout } from "@/lib/chat/chat-layout-persistence";
import {
  markProductsTourCompleted,
  PRODUCTS_TOUR_ID,
  writeProductIntroDismissal,
} from "@/lib/product-onboarding-nudge";
import { isValidOrgId } from "@/lib/utils";

export const TourCard: React.FC<CardComponentProps> = ({
  step,
  currentStep,
  totalSteps,
  nextStep,
  prevStep,
  arrow,
}) => {
  const [, setOnborda] = useAtom(onbordaAtom);
  const router = useRouter();
  const { orgId: routeOrgId } = useParams();
  const orgId = isValidOrgId(routeOrgId as string | undefined)
    ? (routeOrgId as string)
    : undefined;

  const { closeOnborda, currentTour } = useOnborda();
  const isProductsTour = currentTour === PRODUCTS_TOUR_ID;

  const completeProductsTour = (): void => {
    if (orgId) {
      markProductsTourCompleted(orgId);
      writeProductIntroDismissal(orgId, { kind: "permanent" });
    }
    closeOnborda();
  };

  const handleOnClose = (): void => {
    if (isProductsTour) {
      completeProductsTour();
      return;
    }

    closeOnborda();
    setOnborda("Completed");
  };

  const handleCreateProduct = (): void => {
    if (!orgId) {
      handleOnClose();
      return;
    }

    completeProductsTour();
    router.push(
      hrefPreservingPinnedChatLayout(`/${orgId}/products/new`),
    );
  };

  return (
    <Card className="relative z-[999] w-max min-w-[300px] max-w-full border-0 border-none bg-muted">
      <CardHeader>
        <div className="flex w-full items-start justify-between space-x-4">
          <div className="flex flex-col space-y-2">
            <CardDescription className="text-foreground/50">
              {currentStep + 1} of {totalSteps}
            </CardDescription>
            <CardTitle className="mb-2 text-lg text-foreground">
              {step.icon} {step.title}
            </CardTitle>
          </div>
          <Button
            variant="ghost"
            className="absolute right-2 top-4 text-foreground/50 hover:bg-transparent hover:text-foreground/80"
            size="icon"
            onClick={handleOnClose}
          >
            <X size={16} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="text-foreground">{step.content}</CardContent>
      <CardFooter className="text-foreground">
        <div className="flex w-full justify-between gap-4">
          {currentStep !== 0 && (
            <Button onClick={() => prevStep()} variant="default">
              Previous
            </Button>
          )}
          {currentStep + 1 !== totalSteps && (
            <Button onClick={() => nextStep()} variant="default">
              Next
            </Button>
          )}
          {currentStep + 1 === totalSteps && isProductsTour ? (
            <Button onClick={handleCreateProduct} variant="default">
              Create product
            </Button>
          ) : null}
          {currentStep + 1 === totalSteps && !isProductsTour ? (
            <Button onClick={handleOnClose} variant="default">
              Finish!
            </Button>
          ) : null}
        </div>
      </CardFooter>
      <span className="text-[hsl(var(--muted))]">{arrow}</span>
    </Card>
  );
};
